/**
 * Small looping illustrations for InfoPopover (DESIGN.md §4.11 "every label has an {info} popover with one
 * sentence, a tiny example where useful"). Pure SVG + CSS animations in design-token colours; the animations stop
 * under reduced motion (global.css), leaving a meaningful still frame. Each illustration has a caption for
 * screen readers. Unknown ids render nothing.
 */

import type { ReactNode } from 'react';
import { useT } from '../../i18n/useT';
import type { MessageKey } from '../../i18n/types';
import { m } from '../ui.messages';
import s from './Illustration.module.css';

interface Spec {
  readonly caption: MessageKey;
  readonly draw: () => ReactNode;
}

const W = 160;
const H = 84;

const SPECS: Readonly<Record<string, Spec>> = {
  cover: {
    caption: m['illustration.cover'],
    draw: () => (
      <>
        <line className={s.line} x1={10} y1={42} x2={150} y2={42} />
        <rect className={`${s.note} ${s.fallDown}`} x={40} y={4} width={24} height={5} rx={1} />
        <rect className={`${s.note} ${s.hidden} ${s.riseUp}`} x={96} y={74} width={24} height={5} rx={1} />
      </>
    ),
  },
  rotateWithParent: {
    caption: m['illustration.rotateWithParent'],
    draw: () => (
      <g className={s.spin} style={{ transformOrigin: '80px 42px' }}>
        <line className={s.line} x1={30} y1={42} x2={130} y2={42} />
        <circle className={s.pivot} cx={80} cy={42} r={3} />
        <line className={s.childLine} x1={110} y1={22} x2={150} y2={22} />
        <line className={s.link} x1={80} y1={42} x2={110} y2={22} />
      </g>
    ),
  },
  side: {
    caption: m['illustration.side'],
    draw: () => (
      <>
        <line className={s.line} x1={10} y1={42} x2={150} y2={42} />
        <rect className={`${s.note} ${s.fallDown}`} x={40} y={4} width={24} height={5} rx={1} />
        <rect className={`${s.noteAlt} ${s.riseUp}`} x={96} y={74} width={24} height={5} rx={1} />
      </>
    ),
  },
  stayThenJump: {
    caption: m['illustration.stayThenJump'],
    draw: () => (
      <>
        <polyline className={s.guide} points="20,70 140,70" />
        <polyline className={s.curve} points="20,58 110,58 110,22 140,22" />
        <circle className={`${s.dot} ${s.stepDot}`} cx={20} cy={58} r={3.5} />
      </>
    ),
  },
  easingSlice: {
    caption: m['illustration.easingSlice'],
    draw: () => (
      <>
        <path className={s.guideCurve} d="M20 72 C 70 72, 90 12, 140 12" />
        <path className={s.curve} d="M52 64 C 70 56, 84 30, 104 22" />
        <line className={s.sliceMark} x1={52} y1={8} x2={52} y2={78} />
        <line className={s.sliceMark} x1={104} y1={8} x2={104} y2={78} />
      </>
    ),
  },
  fake: {
    caption: m['illustration.fake'],
    draw: () => (
      <>
        <line className={s.line} x1={10} y1={50} x2={150} y2={50} />
        <rect className={`${s.fakeNote} ${s.fallThrough}`} x={68} y={4} width={24} height={5} rx={1} />
      </>
    ),
  },
  speed: {
    caption: m['illustration.speed'],
    draw: () => (
      <>
        <line className={s.line} x1={10} y1={74} x2={150} y2={74} />
        <g className={s.fallFast}>
          <rect className={s.note} x={30} y={8} width={22} height={5} rx={1} />
          <rect className={s.note} x={30} y={34} width={22} height={5} rx={1} />
        </g>
        <g className={s.fallSlow}>
          <rect className={s.noteAlt} x={104} y={30} width={22} height={5} rx={1} />
          <rect className={s.noteAlt} x={104} y={44} width={22} height={5} rx={1} />
        </g>
      </>
    ),
  },
  layers: {
    caption: m['illustration.layers'],
    draw: () => (
      <>
        <polyline className={s.layerA} points="12,64 52,52 92,58 148,40" />
        <polyline className={s.layerB} points="12,70 52,66 92,50 148,62" />
        <polyline className={`${s.curve} ${s.pulse}`} points="12,50 52,34 92,24 148,18" />
      </>
    ),
  },
};

export const ILLUSTRATION_IDS: readonly string[] = Object.keys(SPECS);

export function Illustration(props: { readonly id: string }) {
  const t = useT();
  const spec = SPECS[props.id];
  if (spec === undefined) return null;
  return (
    <figure className={s.figure}>
      <svg className={s.svg} viewBox={`0 0 ${String(W)} ${String(H)}`} role="img" aria-label={t(spec.caption)}>
        {spec.draw()}
      </svg>
      <figcaption className={s.figcaption}>{t(spec.caption)}</figcaption>
    </figure>
  );
}
